import type { TowerDesign } from "@/game/towerSpec";
import type { Section } from "@/physics/model";
import { CATALOG } from "@/game/appurtenances";

/**
 * TOKYO TOWER — 333 m (1958), the Eiffel-inspired red-and-white self-supporting
 * steel lattice symbol of Tokyo. From published data (Wikipedia / Towerpedia):
 *   - Height 333 m; four tapering legs on an ~80 m square base; self-supporting.
 *   - ~4,000 t of steel — ~3,300 t LIGHTER than the Eiffel Tower, thanks to modern
 *     (partly Korean-War-scrap) steel; taller yet lighter.
 *   - Main observation deck at 150 m, top deck at 250 m; antenna mast to 333 m.
 *
 * Built with the flared `radii` profile (like Eiffel but a slimmer, taller curve),
 * `crossarms` for the two observation decks, and a slim antenna mast up top.
 * Modern structural steel (A572-class). Accuracy to the silhouette is the goal.
 */

const TOKYO_LEG: Section = { name: "Tokyo Tower built-up leg", A: 0.09, I: 6.0e-3, dragWidth: 0.55 };
const TOKYO_BRACE: Section = { name: "Tokyo Tower lattice brace", A: 0.013, I: 8e-5, dragWidth: 0.24 };
const DECK: Section = { name: "Tokyo Tower deck truss", A: 0.02, I: 8e-5, dragWidth: 0.32 };

const HEIGHT = 333;
const PANELS = 44;

// Flared four-leg profile: wide 80 m base (circumradius ~40 m) sweeping inward to
// a slim mast by the top deck (250 m), then a thin antenna mast to 333 m.
function profileRadius(y: number): number {
  if (y > 250) return 4 - (4 - 2) * ((y - 250) / (HEIGHT - 250)); // antenna mast
  return 4 + (40 - 4) * Math.pow(1 - y / 250, 1.5); // concave flare 40 → 4 m
}
const radii = Array.from({ length: PANELS + 1 }, (_, i) => profileRadius((i / PANELS) * HEIGHT));

const AZ = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];

export const design: TowerDesign = {
  id: "tokyo-tower",
  name: "Tokyo Tower (Japan)",
  description: "333 m Eiffel-inspired self-supporting steel lattice, 1958 — lighter than Eiffel. Publicly approximated.",
  author: "iconic tower — approximated from public data",
  spec: {
    type: "lattice",
    height: HEIGHT,
    stack: {
      faces: 4,
      panels: PANELS,
      baseRadius: 40, // ~80 m square base
      topRadius: 2,
      radii,
      legSection: TOKYO_LEG,
      braceSection: TOKYO_BRACE,
      legMaterial: "a572",
      braceMaterial: "a572",
      bracing: "X",
    },
    // the two observation decks
    crossarms: [
      { atHeightFrac: 150 / HEIGHT, radius: 15, section: DECK, material: "a572" }, // main deck 150 m
      { atHeightFrac: 250 / HEIGHT, radius: 8, section: DECK, material: "a572" }, // top deck 250 m
    ],
    foundation: {
      soil: "stiff-clay", // Shiba, Tokyo — firm ground; legs tied underground by 20×5 cm steel rods
      base: { type: "pad", width: 10, diameter: 2, depth: 4, concrete: "c30" },
      connection: {
        kind: "anchorBolts", nBolts: 20, bolt: "b2_25", grade: "a615_75",
        boltCircleDia: 3.0, embedment: 1.2, edgeDistance: 0.8,
        platePlanArea: 9, plateThickness: 0.08, plateGrade: "a36rod", fc: 30e6,
      },
    },
    // broadcast antennas near the top
    loads: [
      ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSector, height: 320, azimuth })),
      { appurtenance: CATALOG.coaxBundle, height: 320, runLength: 320 },
    ],
  },
};
